import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

const API_BASE = 'https://visionry-api-production.up.railway.app';

// Mobile breakpoint — below this, action buttons drop to a full-width row.
const MOBILE_MAX = 640;
function useIsMobile() {
  const [m, setM] = useState(typeof window !== 'undefined' ? window.innerWidth <= MOBILE_MAX : false);
  useEffect(() => {
    const onR = () => setM(window.innerWidth <= MOBILE_MAX);
    window.addEventListener('resize', onR);
    return () => window.removeEventListener('resize', onR);
  }, []);
  return m;
}

// ── Brand tokens (match visionry.io) ──
const C = {
  bg:       '#061618',
  card:     '#0d3338',
  card2:    '#10434a',
  cardDim:  '#0a282c',
  border:   '#10434a',
  borderHi: '#1d8a9a',
  teal:     '#25a2b3',
  tealSoft: '#5fc4d2',
  text:     '#e0f0f2',
  muted:    '#7fb8c0',
  faint:    '#3d5a5f',
  green:    '#00cc88',
  greenBg:  '#06231a',
  red:      '#ff6b6b',
  redBorder:'#a13b3b',
  // Warm tones for "needs attention" — flagged for later touch-up against teal.
  amber:    '#ffaa44',
  amberLine:'#cc7a1f',
  amberBg:  '#241803'
};

// ─── helpers ──────────────────────────────────────────────────────────────

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', {
    timeZone: 'Europe/Dublin', day: '2-digit', month: 'short', year: '2-digit'
  });
}

function lastLessonLabel(lastIso, hasUpcoming) {
  if (lastIso) return `last ${fmtDate(lastIso)}`;
  if (hasUpcoming) return 'upcoming lesson booked';
  return 'no lessons yet';
}

// A student's EDT identity is complete only when all four fields are present.
function edtComplete(s) {
  return !!(s.driver_number && s.date_of_birth && s.logbook_number && s.permit_expiry);
}

// Convert a stored 'YYYY-MM-DD' date to the DD/MM/YYYY the instructor types.
function isoToDmy(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}
// dd/mm/yyyy from a 'YYYY-MM-DD' string (worksheet display).
function dmyFromIso(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}
// Parse DD/MM/YYYY → 'YYYY-MM-DD', or null if invalid.
function dmyToIso(raw) {
  const m = (raw || '').trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return null;
  const day = +m[1], month = +m[2], year = +m[3];
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const dt = new Date(Date.UTC(year, month - 1, day));
  if (dt.getUTCFullYear() !== year || dt.getUTCMonth() !== month - 1 || dt.getUTCDate() !== day) return null;
  const pad = n => String(n).padStart(2, '0');
  return `${year}-${pad(month)}-${pad(day)}`;
}

function StatusPill({ status }) {
  const map = {
    active:   { bg: C.greenBg,  fg: C.green, label: 'Active'   },
    paused:   { bg: C.amberBg,  fg: C.amber, label: 'Paused'   },
    blocked:  { bg: '#2a0f0f',  fg: C.red,   label: 'Blocked'  },
    archived: { bg: C.cardDim,  fg: C.muted, label: 'Finished' }
  };
  const s = map[status] || map.active;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 9px', borderRadius: 20,
      fontSize: 11, fontWeight: 600, background: s.bg, color: s.fg
    }}>
      {s.label}
    </span>
  );
}

// Shared action button. `fill` makes it expand to share a row equally (used on
// mobile so a row of actions splits the width into proper thumb targets).
// Sizing follows the ~44px mobile touch-target standard (was 12px/5px — too
// small to tap comfortably on a phone).
function Btn({ children, onClick, color = C.muted, border = C.border, disabled, fill }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        flex: fill ? 1 : 'none',
        background: 'none', border: `1px solid ${border}`, borderRadius: 10,
        color, fontSize: 14, fontWeight: 600, padding: '11px 16px',
        minHeight: 44, whiteSpace: 'nowrap',
        cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1
      }}
    >
      {children}
    </button>
  );
}

function Field({ label, value, onChange, placeholder, hint }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', fontSize: 12, color: C.muted, marginBottom: 4 }}>{label}</label>
      <input
        value={value}
        placeholder={placeholder}
        onChange={e => onChange(e.target.value)}
        style={{
          width: '100%', padding: '8px 10px', background: C.bg,
          border: `1px solid ${C.border}`, borderRadius: 8, color: C.text,
          fontSize: 14, outline: 'none', boxSizing: 'border-box'
        }}
      />
      {hint && <div style={{ fontSize: 11, color: C.faint, marginTop: 3 }}>{hint}</div>}
    </div>
  );
}

// ─── EDT worksheet (per student) ────────────────────────────────────────────
// Computes EDT progress from COMPLETED bookings in date order, formats each
// row exactly as the MyEDT web form wants, flags RSA-rule problems, and lets
// the instructor tick off rows already filed in the portal.

function EdtWorksheet({ student }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);

  const programmeLength = student.edt_type === 'reduced' ? 6 : 12;
  const startNumber     = student.edt_start_number || 1;

  const load = useCallback(async () => {
    // Completed lessons, oldest first → that order IS the EDT sequence
    // (the Nth completed lesson = EDT lesson N, with the start offset applied).
    const { data: completed } = await supabase
      .from('bookings')
      .select('booking_id, start_iso, edt_filed')
      .eq('wa_id', student.wa_id)
      .eq('status', 'booked')
      .eq('completion_state', 'completed')
      .order('start_iso', { ascending: true });

    const list = (completed || []).map((b, i) => {
      const lessonNumber = startNumber + i;
      const dateIso = b.start_iso.slice(0, 10);
      return {
        booking_id:     b.booking_id,
        lessonNumber,
        completionDate: dmyFromIso(dateIso),
        rawDate:        dateIso,
        edt_filed:      b.edt_filed,
        overLimit:      lessonNumber > programmeLength,
        warnings:       []
      };
    });

    // Pre-flag the RSA portal's notification risks before the instructor types them.
    const seenDates = {};
    for (const r of list) seenDates[r.rawDate] = (seenDates[r.rawDate] || 0) + 1;
    list.forEach(r => {
      if (seenDates[r.rawDate] > 1) r.warnings.push('Two lessons same date'); // Threshold Breach
      if (r.overLimit)             r.warnings.push(`Beyond lesson ${programmeLength}`);
    });

    setRows(list);
  }, [student.wa_id, startNumber, programmeLength]);

  useEffect(() => { load(); }, [load]);

  async function toggleFiled(row) {
    setBusy(row.booking_id);
    await supabase
      .from('bookings')
      .update({ edt_filed: !row.edt_filed, updated_at: new Date().toISOString() })
      .eq('booking_id', row.booking_id);
    await load();
    setBusy(null);
  }

  if (!rows) return <div style={{ color: C.faint, fontSize: 13, padding: '8px 0' }}>Loading progress…</div>;

  const withinProgramme = rows.filter(r => r.lessonNumber <= programmeLength);
  const doneCount = withinProgramme.length;
  const idComplete = student.driver_number && student.date_of_birth && student.logbook_number;

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ fontSize: 12, color: C.teal, textTransform: 'uppercase', letterSpacing: 1 }}>
          EDT worksheet
        </div>
        <div style={{ fontSize: 13, color: doneCount >= programmeLength ? C.green : C.muted, fontWeight: 600 }}>
          {doneCount} of {programmeLength} done
        </div>
      </div>

      {!idComplete && (
        <div style={{
          background: C.amberBg, border: `1px solid ${C.amberLine}`, borderRadius: 8,
          padding: '8px 10px', color: C.amber, fontSize: 12, marginBottom: 10
        }}>
          ⚠ Add driver number, DOB and logbook number above — the portal needs all three for every lesson.
        </div>
      )}

      {rows.length === 0 ? (
        <div style={{ color: C.faint, fontSize: 13, padding: '8px 0' }}>
          No completed lessons yet. Mark lessons complete on the Today tab and they'll appear here.
        </div>
      ) : (
        <>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 8 }}>
            Type each row into the MyEDT portal, then tick it off. Driver number, logbook and DOB are the same every row.
          </div>
          <div style={{ display: 'flex', fontSize: 10, color: C.muted, padding: '0 4px 6px', gap: 8, borderBottom: `1px solid ${C.border}` }}>
            <span style={{ width: 24 }}></span>
            <span style={{ width: 56 }}>Lesson</span>
            <span style={{ flex: 1 }}>Completion date</span>
          </div>
          {rows.map(r => (
            <div key={r.booking_id} style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '8px 4px',
              borderBottom: `1px solid ${C.cardDim}`, opacity: r.edt_filed ? 0.5 : 1
            }}>
              <input type="checkbox" checked={r.edt_filed} disabled={busy === r.booking_id}
                     onChange={() => toggleFiled(r)} style={{ width: 18, height: 18, cursor: 'pointer', accentColor: C.teal }} />
              <span style={{ width: 56, fontSize: 13, fontWeight: 700, color: C.tealSoft, textDecoration: r.edt_filed ? 'line-through' : 'none' }}>
                Lesson {r.lessonNumber}
              </span>
              <span style={{ flex: 1, fontSize: 13, color: C.text, textDecoration: r.edt_filed ? 'line-through' : 'none' }}>
                {r.completionDate}
                {r.warnings.length > 0 && (
                  <span style={{ marginLeft: 8, fontSize: 10, color: C.amber }}>
                    ⚠ {r.warnings.join(' · ')}
                  </span>
                )}
              </span>
            </div>
          ))}

          <div style={{ marginTop: 12, padding: 10, background: C.cardDim, borderRadius: 8, fontSize: 12, color: C.text }}>
            <div style={{ marginBottom: 4 }}><span style={{ color: C.muted }}>Driver number:</span> {student.driver_number || '—'}</div>
            <div style={{ marginBottom: 4 }}><span style={{ color: C.muted }}>DOB:</span> {student.date_of_birth ? dmyFromIso(student.date_of_birth) : '—'}</div>
            <div><span style={{ color: C.muted }}>Logbook number:</span> {student.logbook_number || '—'}</div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── EDT panel (inline, per student) ───────────────────────────────────────

function EdtPanel({ student, onSaved }) {
  const initialStart = student.edt_start_number || 1;
  const [form, setForm] = useState({
    driver_number:  student.driver_number || '',
    dob:            isoToDmy(student.date_of_birth),
    logbook_number: student.logbook_number || '',
    permit_expiry:  isoToDmy(student.permit_expiry),
    edt_type:       student.edt_type || 'full',
    // Toggle is derived: a start number above 1 means they had prior lessons.
    prior:          initialStart > 1,
    edt_start:      String(initialStart)
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg]       = useState(null);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); setMsg(null); }

  // Toggling "prior instructor". Turning OFF resets start to 1 so we never save
  // a stale start number while the field is hidden. Turning ON clears the "1"
  // so the instructor types the real number into an empty field.
  function setPrior(prior) {
    setForm(f => ({ ...f, prior, edt_start: prior ? (f.edt_start === '1' ? '' : f.edt_start) : '1' }));
    setMsg(null);
  }

  const programmeLength = form.edt_type === 'reduced' ? 6 : 12;

  async function save() {
    // Identity fields.
    const driver = form.driver_number.replace(/\s+/g, '');
    if (driver && !/^\d{9}$/.test(driver)) {
      return setMsg({ type: 'err', text: 'Driver number must be 9 digits.' });
    }
    const dobIso = form.dob ? dmyToIso(form.dob) : null;
    if (form.dob && !dobIso) {
      return setMsg({ type: 'err', text: 'Date of birth must be DD/MM/YYYY.' });
    }
    const expiryIso = form.permit_expiry ? dmyToIso(form.permit_expiry) : null;
    if (form.permit_expiry && !expiryIso) {
      return setMsg({ type: 'err', text: 'Permit expiry must be DD/MM/YYYY.' });
    }
    const logbook = form.logbook_number.trim();
    if (logbook && !/^\d{1,6}$/.test(logbook)) {
      return setMsg({ type: 'err', text: 'Logbook number is up to 6 digits.' });
    }

    // Start number: only relevant when "prior instructor" is Yes; else forced to 1.
    let startNum = 1;
    if (form.prior) {
      startNum = parseInt(form.edt_start, 10);
      if (!Number.isInteger(startNum) || startNum < 2 || startNum > programmeLength) {
        return setMsg({ type: 'err', text: `Start lesson must be between 2 and ${programmeLength}.` });
      }
    }

    setSaving(true);
    const { error } = await supabase
      .from('students')
      .update({
        driver_number:    driver || null,
        date_of_birth:    dobIso,
        logbook_number:   logbook ? logbook.padStart(6, '0') : null,
        permit_expiry:    expiryIso,
        edt_type:         form.edt_type,
        edt_start_number: startNum,
        updated_at:       new Date().toISOString()
      })
      .eq('wa_id', student.wa_id);
    setSaving(false);

    if (error) return setMsg({ type: 'err', text: error.message });
    setMsg({ type: 'ok', text: 'Saved ✓' });
    onSaved();
  }

  return (
    <div style={{ marginTop: 12, padding: 16, background: C.bg, borderRadius: 10, border: `1px solid ${C.border}` }}>
      <div style={{ fontSize: 12, color: C.teal, marginBottom: 12, textTransform: 'uppercase', letterSpacing: 1 }}>
        EDT details
      </div>
      <Field label="Driver number" value={form.driver_number}
             onChange={v => set('driver_number', v)} placeholder="9 digits"
             hint="From the student's learner permit (field 5). They gave this at signup — fix here if wrong." />
      <Field label="Date of birth" value={form.dob}
             onChange={v => set('dob', v)} placeholder="DD/MM/YYYY" />
      <Field label="Logbook number" value={form.logbook_number}
             onChange={v => set('logbook_number', v)} placeholder="6 digits"
             hint="You assign this from your logbook stock when uploading Lesson 1." />
      <Field label="Permit expiry" value={form.permit_expiry}
             onChange={v => set('permit_expiry', v)} placeholder="DD/MM/YYYY"
             hint="Check against the physical permit at the first lesson." />

      {/* EDT programme type — defaults to Full 12 */}
      <div style={{ marginBottom: 12 }}>
        <label style={{ display: 'block', fontSize: 12, color: C.muted, marginBottom: 4 }}>EDT programme</label>
        <div style={{ display: 'flex', gap: 8 }}>
          <SegBtn active={form.edt_type !== 'reduced'} onClick={() => set('edt_type', 'full')}>Full · 12</SegBtn>
          <SegBtn active={form.edt_type === 'reduced'} onClick={() => set('edt_type', 'reduced')}>Reduced · 6</SegBtn>
        </div>
        <div style={{ fontSize: 11, color: C.faint, marginTop: 3 }}>
          Reduced (6) is for foreign-licence holders. Most students are Full (12).
        </div>
      </div>

      {/* Prior-instructor gate — only reveals the start field when Yes */}
      <div style={{ marginBottom: 12 }}>
        <label style={{ display: 'block', fontSize: 12, color: C.muted, marginBottom: 4 }}>
          Did any EDT lessons with a previous instructor?
        </label>
        <div style={{ display: 'flex', gap: 8 }}>
          <SegBtn active={!form.prior} onClick={() => setPrior(false)}>No</SegBtn>
          <SegBtn active={form.prior}  onClick={() => setPrior(true)}>Yes</SegBtn>
        </div>
      </div>

      {form.prior && (
        <Field label="Start at lesson #" value={form.edt_start}
               onChange={v => set('edt_start', v.replace(/\D/g, ''))} placeholder={`2–${programmeLength}`}
               hint="The lesson number they continue from with you (e.g. did 1–4 elsewhere → start at 5)." />
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 4 }}>
        <button onClick={save} disabled={saving}
          style={{
            background: C.teal, border: 'none', borderRadius: 8, color: C.bg,
            fontSize: 14, fontWeight: 600, padding: '11px 20px', minHeight: 44,
            cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1
          }}>
          {saving ? 'Saving…' : 'Save EDT details'}
        </button>
        {msg && <span style={{ fontSize: 13, color: msg.type === 'ok' ? C.green : C.red }}>{msg.text}</span>}
      </div>

      <EdtWorksheet student={student} />
    </div>
  );
}

function SegBtn({ active, onClick, children }) {
  return (
    <button onClick={onClick} style={{
      flex: 1, background: active ? C.teal : 'none',
      border: `1px solid ${active ? C.teal : C.border}`, borderRadius: 8,
      color: active ? C.bg : C.muted, fontSize: 13, fontWeight: active ? 600 : 400,
      padding: '10px', minHeight: 44, cursor: 'pointer'
    }}>{children}</button>
  );
}

// ─── main ─────────────────────────────────────────────────────────────────

export default function Students({ instructor }) {
  const [students, setStudents]   = useState(null);
  const [lastLesson, setLastLesson] = useState({});
  const [hasFuture, setHasFuture]   = useState({});
  const [busy, setBusy]           = useState(null);
  const [note, setNote]           = useState(null);
  const [expanded, setExpanded]   = useState(null); // wa_id of expanded student
  const [archiveConfirm, setArchiveConfirm] = useState(null); // wa_id awaiting archive confirm
  const [showFinished, setShowFinished]     = useState(false); // collapsed "Finished" section
  const isMobile = useIsMobile();

  const load = useCallback(async () => {
    const { data: studs } = await supabase
      .from('students')
      .select('wa_id, name, has_own_car, lesson_count, approval_status, status, created_at, driver_number, date_of_birth, logbook_number, permit_expiry, edt_type, edt_start_number')
      .eq('instructor_key', instructor.instructor_key)
      .order('created_at', { ascending: false });

    const nowIso = new Date().toISOString();
    const { data: allBookings } = await supabase
      .from('bookings')
      .select('wa_id, start_iso')
      .eq('instructor_key', instructor.instructor_key)
      .eq('status', 'booked')
      .order('start_iso', { ascending: false });

    const lastMap = {}, futureSet = {};
    for (const b of (allBookings || [])) {
      if (b.start_iso < nowIso) {
        if (!lastMap[b.wa_id]) lastMap[b.wa_id] = b.start_iso;
      } else {
        futureSet[b.wa_id] = true;
      }
    }

    setLastLesson(lastMap);
    setHasFuture(futureSet);
    setStudents(studs || []);
  }, [instructor.instructor_key]);

  useEffect(() => { load(); }, [load]);

  async function update(waId, fields) {
    setBusy(waId); setNote(null);
    await supabase.from('students').update({ ...fields, updated_at: new Date().toISOString() }).eq('wa_id', waId);
    await load();
    setBusy(null);
  }

  // Archive (= mark finished) and restore. Soft, reversible: just a status flip.
  // Off the cap, out of the active list, can't book — but data kept and undoable.
  async function archive(waId) {
    setArchiveConfirm(null);
    await update(waId, { status: 'archived' });
  }
  async function restore(waId) {
    await update(waId, { status: 'active' });
  }

  async function approve(waId) {
    setBusy(waId); setNote(null);
    await supabase
      .from('students')
      .update({ approval_status: 'approved', status: 'active', updated_at: new Date().toISOString() })
      .eq('wa_id', waId);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${API_BASE}/notify-approved`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
        body: JSON.stringify({ wa_id: waId })
      });
      if (!res.ok) setNote('Student approved — but the WhatsApp notification didn’t send. They can still book.');
    } catch {
      setNote('Student approved — but the WhatsApp notification didn’t send. They can still book.');
    }
    await load();
    setBusy(null);
  }

  if (!students) return <div style={{ color: C.muted, padding: 20, fontFamily: 'Poppins, sans-serif' }}>Loading…</div>;

  const pending  = students.filter(s => s.approval_status === 'pending');
  // Active = approved (not pending/declined) AND not archived.
  const active   = students.filter(s =>
    s.approval_status !== 'pending' && s.approval_status !== 'declined' && s.status !== 'archived'
  );
  // Finished = archived students (regardless of approval), kept for reference + restore.
  const archived = students.filter(s => s.status === 'archived' && s.approval_status !== 'declined');

  return (
    <div style={{ fontFamily: 'Poppins, sans-serif' }}>
      <div style={{ fontSize: 11, color: C.muted, marginBottom: 16, textTransform: 'uppercase', letterSpacing: 1 }}>
        Students
      </div>

      {note && (
        <div style={{
          background: C.amberBg, border: `1px solid ${C.amberLine}`, borderRadius: 8,
          padding: '10px 12px', color: C.amber, fontSize: 13, marginBottom: 16
        }}>{note}</div>
      )}

      {/* ── Pending approval queue ── */}
      {pending.length > 0 && (
        <div style={{
          background: C.card, border: `1px solid ${C.amberLine}`,
          borderRadius: 14, padding: 20, marginBottom: 16
        }}>
          <div style={{ fontSize: 13, color: C.amber, marginBottom: 12, fontWeight: 600 }}>
            ⏳ Waiting for your approval ({pending.length})
          </div>
          {pending.map(s => (
            <div key={s.wa_id} style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              justifyContent: 'space-between',
              alignItems: isMobile ? 'stretch' : 'center',
              padding: '12px 0', borderBottom: `1px solid ${C.border}`, gap: 12
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{s.name || 'No name yet'}</div>
                <div style={{ fontSize: 12, color: C.muted }}>{s.wa_id} · joined {fmtDate(s.created_at)}</div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Btn onClick={() => approve(s.wa_id)} color={C.green} border={C.green} disabled={busy === s.wa_id} fill={isMobile}>✓ Approve</Btn>
                <Btn onClick={() => update(s.wa_id, { approval_status: 'declined' })} color={C.red} border={C.redBorder} disabled={busy === s.wa_id} fill={isMobile}>Decline</Btn>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Active students ── */}
      <div style={{
        background: C.card, border: `1px solid ${C.border}`,
        borderRadius: 14, padding: 20
      }}>
        <div style={{ fontSize: 13, color: C.muted, marginBottom: 12 }}>
          Your students ({active.length})
        </div>

        {active.length === 0 && (
          <div style={{ color: C.faint, fontSize: 14, padding: '8px 0' }}>No students yet.</div>
        )}

        {active.map(s => {
          const isOpen     = expanded === s.wa_id;
          const incomplete = !edtComplete(s);
          return (
            <div key={s.wa_id} style={{ padding: '12px 0', borderBottom: `1px solid ${C.border}` }}>
              <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center', justifyContent: 'space-between', gap: isMobile ? 12 : 10 }}>
                <div
                  style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
                  onClick={() => setExpanded(isOpen ? null : s.wa_id)}
                >
                  <div style={{ fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8, color: C.text }}>
                    <span style={{ color: C.muted, fontSize: 12 }}>{isOpen ? '▼' : '▸'}</span>
                    {s.name || 'No name'}
                    <StatusPill status={s.status || 'active'} />
                    {incomplete && (
                      <span title="EDT details incomplete — add the missing info"
                            style={{ color: C.amber, fontSize: 13 }}>⚠</span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: C.muted, marginTop: 3 }}>
                    📞 {s.wa_id} · {s.lesson_count || 0} lessons · {lastLessonLabel(lastLesson[s.wa_id], hasFuture[s.wa_id])}
                    {' · '}{s.has_own_car === 'yes' ? '🚗 own car' : "instructor's car"}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexShrink: 0, width: isMobile ? '100%' : 'auto' }}>
                  {s.status === 'blocked' ? (
                    <Btn onClick={() => update(s.wa_id, { status: 'active' })} color={C.green} border={C.green} disabled={busy === s.wa_id} fill={isMobile}>Unblock</Btn>
                  ) : (
                    <>
                      {s.status === 'paused'
                        ? <Btn onClick={() => update(s.wa_id, { status: 'active' })} disabled={busy === s.wa_id} fill={isMobile}>Resume</Btn>
                        : <Btn onClick={() => update(s.wa_id, { status: 'paused' })} disabled={busy === s.wa_id} fill={isMobile}>Pause</Btn>}
                      <Btn onClick={() => update(s.wa_id, { status: 'blocked' })} color={C.red} border={C.redBorder} disabled={busy === s.wa_id} fill={isMobile}>Block</Btn>
                    </>
                  )}
                </div>
              </div>

              {isOpen && (
                <>
                  <EdtPanel student={s} onSaved={load} />

                  {/* ── Archive (mark finished) — lifecycle action, lives in the
                       expanded area below the EDT panel. Light inline confirm. ── */}
                  <div style={{
                    marginTop: 12, paddingTop: 12, borderTop: `1px solid ${C.cardDim}`,
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    alignItems: isMobile ? 'stretch' : 'center',
                    justifyContent: 'space-between', gap: 10
                  }}>
                    <div style={{ fontSize: 12, color: C.muted }}>
                      Finished lessons or passed their test? Mark them finished to free up your space.
                    </div>
                    {archiveConfirm === s.wa_id ? (
                      <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                        <Btn onClick={() => archive(s.wa_id)} color={C.muted} border={C.borderHi} disabled={busy === s.wa_id} fill={isMobile}>
                          {busy === s.wa_id ? 'Saving…' : 'Confirm'}
                        </Btn>
                        <Btn onClick={() => setArchiveConfirm(null)} disabled={busy === s.wa_id} fill={isMobile}>Cancel</Btn>
                      </div>
                    ) : (
                      <Btn onClick={() => setArchiveConfirm(s.wa_id)} color={C.muted} border={C.borderHi} fill={isMobile}>Mark as finished</Btn>
                    )}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Finished (archived) students — collapsed section ── */}
      {archived.length > 0 && (
        <div style={{
          background: C.cardDim, border: `1px solid ${C.border}`,
          borderRadius: 14, padding: 20, marginTop: 16
        }}>
          <div
            style={{ fontSize: 13, color: C.muted, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}
            onClick={() => setShowFinished(v => !v)}
          >
            <span style={{ color: C.muted, fontSize: 12 }}>{showFinished ? '▼' : '▸'}</span>
            Finished ({archived.length})
          </div>

          {showFinished && archived.map(s => {
            const isOpen = expanded === s.wa_id;
            return (
              <div key={s.wa_id} style={{ padding: '12px 0', borderBottom: `1px solid ${C.border}` }}>
                <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center', justifyContent: 'space-between', gap: isMobile ? 12 : 10 }}>
                  <div
                    style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
                    onClick={() => setExpanded(isOpen ? null : s.wa_id)}
                  >
                    <div style={{ fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8, color: C.muted }}>
                      <span style={{ color: C.muted, fontSize: 12 }}>{isOpen ? '▼' : '▸'}</span>
                      {s.name || 'No name'}
                      <StatusPill status="archived" />
                    </div>
                    <div style={{ fontSize: 12, color: C.muted, marginTop: 3 }}>
                      📞 {s.wa_id} · {s.lesson_count || 0} lessons · {lastLessonLabel(lastLesson[s.wa_id], hasFuture[s.wa_id])}
                    </div>
                  </div>
                  <div style={{ flexShrink: 0, width: isMobile ? '100%' : 'auto' }}>
                    <Btn onClick={() => restore(s.wa_id)} color={C.green} border={C.green} disabled={busy === s.wa_id} fill={isMobile}>Restore</Btn>
                  </div>
                </div>

                {isOpen && <EdtPanel student={s} onSaved={load} />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}